import React, { useState } from "react";
import {
  ArrowBack,
  ArrowForward,
  AutoStories,
  CheckCircle,
  Cloud,
  Explore,
  Flight,
  Gavel,
  Bookmark,
  Person,
  Replay,
  MenuBook,
  Route,
  Security,
  Settings,
  Timer,
} from "@mui/icons-material";
import { Box, Button, Chip, Container, Divider, Grid, Paper, Stack, Typography } from "@mui/material";

const accent = "#ffb84d";

const getSectionIcon = (section) => {
  if (section.startsWith("Człowiek")) return <Person />;
  if (section === "Meteorologia") return <Cloud />;
  if (section === "Nawigacja" || section.includes("planowanie")) return <Route />;
  if (section === "Prawo lotnicze") return <Gavel />;
  if (section.includes("Bezpieczeństwo")) return <Security />;
  if (section.includes("Procedury")) return <CheckCircle />;
  if (section.includes("Transport") || section.includes("Obsługa")) return <Settings />;
  return <Flight />;
};

function StartScreen({ onStartPractice, onStartTest, onStartSectionPractice, onReviewAll, onStartIncorrectPractice, onStartIncorrectTest, onStartSavedTest, incorrectCount, savedCount, sections }) {
  const [showSections, setShowSections] = useState(false);

  if (showSections) {
    return (
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Button startIcon={<ArrowBack />} onClick={() => setShowSections(false)} sx={{ mb: 3, color: "text.secondary" }}>
          Wróć do wyboru trybu
        </Button>
        <Box sx={{ maxWidth: 720, mb: 4 }}>
          <Chip icon={<MenuBook />} label="Nauka po działach" sx={{ mb: 2, bgcolor: "rgba(45, 92, 88, .1)", color: "primary.main" }} />
          <Typography variant="h2" component="h1" sx={{ fontSize: { xs: "2.1rem", md: "3.25rem" }, mb: 1.5 }}>
            Wybierz temat
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ fontSize: "1.05rem" }}>
            Skup się na jednym obszarze, odpowiadaj bez presji czasu i buduj pewność krok po kroku.
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {sections.map((section, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={section}>
              <Paper
                component="button"
                onClick={() => onStartSectionPractice(section)}
                elevation={0}
                sx={{
                  width: "100%", minHeight: 154, p: 2.5, textAlign: "left", cursor: "pointer", borderRadius: 1.5,
                  border: "1px solid", borderColor: "rgba(23, 52, 52, .11)", bgcolor: "background.paper",
                  transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease",
                  "&:hover": { transform: "translateY(-3px)", boxShadow: "0 12px 25px rgba(24, 55, 55, .10)", borderColor: "primary.main" },
                }}
              >
                <Stack height="100%" justifyContent="space-between" spacing={2}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 36, height: 36, borderRadius: 1, display: "grid", placeItems: "center", bgcolor: index % 2 ? "rgba(255,184,77,.22)" : "rgba(45,92,88,.12)", color: "primary.main" }}>
                        {getSectionIcon(section)}
                      </Box>
                      <Typography variant="caption" color="text.secondary">{String(index + 1).padStart(2, "0")}</Typography>
                    </Stack>
                    <ArrowForward fontSize="small" color="action" />
                  </Box>
                  <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 750, lineHeight: 1.35 }}>{section}</Typography>
                </Stack>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  return (
    <Box sx={{ overflow: "hidden" }}>
      <Box component="header" sx={{ borderBottom: "1px solid rgba(23,52,52,.09)", bgcolor: "rgba(255,255,255,.67)", backdropFilter: "blur(12px)" }}>
        <Container maxWidth="lg" sx={{ minHeight: 72, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Box sx={{ width: 38, height: 38, display: "grid", placeItems: "center", borderRadius: 1, color: "#143536", bgcolor: accent }}><Flight /></Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, letterSpacing: "-.02em" }}>Pilot Quiz</Typography>
          </Stack>
          <Chip size="small" label="ŚKPP" sx={{ fontWeight: 800, bgcolor: "rgba(45,92,88,.1)", color: "primary.main" }} />
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 7 } }}>
        <Grid container spacing={{ xs: 4, md: 7 }} alignItems="center">
          <Grid size={{ xs: 12, md: 7 }}>
            <Chip label="PRZYGOTUJ SIĘ DO EGZAMINU" sx={{ mb: 2.5, color: "primary.main", bgcolor: "rgba(45,92,88,.1)", fontWeight: 800, fontSize: ".7rem", letterSpacing: ".08em" }} />
            <Typography variant="h1" component="h1" sx={{ fontSize: { xs: "3rem", sm: "4rem", md: "5.2rem" }, lineHeight: .98, maxWidth: 650 }}>
              Lataj pewnie.<br />Zdaj spokojnie.
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 3, maxWidth: 560, fontSize: { xs: "1rem", md: "1.15rem" }, lineHeight: 1.65 }}>
              Prosty trening do świadectwa kwalifikacji pilota paralotni. Zacznij od pełnego testu lub ucz się dokładnie tego, czego potrzebujesz.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 4 }}>
              <Button onClick={onStartTest} variant="contained" size="large" endIcon={<ArrowForward />} sx={{ px: 3, py: 1.45, borderRadius: 1, bgcolor: "primary.main" }}>
                Rozpocznij próbny egzamin
              </Button>
              <Button onClick={() => onStartPractice()} variant="text" size="large" sx={{ px: 2, py: 1.45, color: "primary.main" }}>
                Ćwicz bez limitu
              </Button>
            </Stack>
            <Stack direction="row" spacing={{ xs: 2, sm: 3 }} sx={{ mt: 4 }} divider={<Divider orientation="vertical" flexItem />}>
              <Box><Typography sx={{ fontWeight: 800, fontSize: "1.2rem"}}>20 pytań</Typography><Typography variant="caption" color="text.secondary">w teście</Typography></Box>
              <Box><Typography sx={{ fontWeight: 800, fontSize: "1.2rem"}}>20 min</Typography><Typography variant="caption" color="text.secondary">na rozwiązanie</Typography></Box>
              <Box><Typography sx={{ fontWeight: 800, fontSize: "1.2rem"}}>{sections.length} działów</Typography><Typography variant="caption" color="text.secondary">do opanowania</Typography></Box>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 2, bgcolor: "#1d4848", color: "#f8fbf8", position: "relative", overflow: "hidden", boxShadow: "0 22px 55px rgba(25,65,65,.22)" }}>
              <Box sx={{ position: "absolute", width: 240, height: 240, borderRadius: "50%", bgcolor: "rgba(255,184,77,.18)", right: -80, top: -105 }} />
              <Box sx={{ position: "relative" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center"><Typography sx={{ fontWeight: 800 }}>Symulacja egzaminu</Typography><Timer sx={{ color: accent }} /></Stack>
                <Typography sx={{ mt: 4, color: "rgba(248,251,248,.65)", fontSize: ".84rem" }}>PYTANIE 08 / 20</Typography>
                <Typography variant="h5" sx={{ mt: 1, fontWeight: 700, lineHeight: 1.35 }}>Kiedy pilot powinien zrezygnować z planowanego lotu?</Typography>
                <Stack spacing={1.25} sx={{ mt: 3 }}>
                  {["Gdy warunki są niepewne", "Po sprawdzeniu sprzętu", "Wyłącznie po zachodzie słońca"].map((answer, index) => <Box key={answer} sx={{ p: 1.35, borderRadius: 1, bgcolor: index === 0 ? "rgba(255,184,77,.96)" : "rgba(255,255,255,.1)", color: index === 0 ? "#173434" : "#f8fbf8", fontWeight: 650, fontSize: ".9rem" }}>{String.fromCharCode(65 + index)} &nbsp; {answer}</Box>)}
                </Stack>
                <Box sx={{ mt: 3, height: 5, borderRadius: 999, bgcolor: "rgba(255,255,255,.16)" }}><Box sx={{ width: "40%", height: "100%", borderRadius: 999, bgcolor: accent }} /></Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 6, md: 10 } }}>
          <Typography variant="h3" component="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}>Wybierz swój sposób nauki</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Każdy tryb prowadzi Cię prosto do celu.</Typography>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            {[
              { title: "Nauka działami", text: "Skup się na konkretnym temacie.", icon: <MenuBook />, action: () => setShowSections(true), label: "Wybierz dział" },
              { title: "Szybka praktyka", text: "Wszystkie pytania, bez zegara.", icon: <AutoStories />, action: () => onStartPractice(), label: "Zacznij ćwiczyć" },
              { title: "Baza odpowiedzi", text: "Przejrzyj pytania i poprawne odpowiedzi.", icon: <CheckCircle />, action: onReviewAll, label: "Otwórz bazę" },
            ].map((item) => (
              <Grid size={{ xs: 12, md: 4 }} key={item.title}>
                <Paper component="button" type="button" onClick={item.action} elevation={0} sx={{ p: 3, height: "100%", width: "100%", textAlign: "left", cursor: "pointer", font: "inherit", border: "1px solid rgba(23,52,52,.1)", bgcolor: "rgba(255,255,255,.72)", borderRadius: 1.5, transition: "transform .18s ease, box-shadow .18s ease, border-color .18s ease", "&:hover": { transform: "translateY(-3px)", boxShadow: "0 12px 25px rgba(24, 55, 55, .10)", borderColor: "primary.main" }, "&:focus-visible": { outline: "3px solid rgba(36,91,89,.4)", outlineOffset: 3 } }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: 1, display: "grid", placeItems: "center", color: "primary.main", bgcolor: "rgba(45,92,88,.1)" }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: .5, minHeight: 48 }}>{item.text}</Typography>
                  <Stack direction="row" alignItems="center" spacing={.5} sx={{ mt: 1, color: "primary.main", fontWeight: 700 }}><Typography component="span">{item.label}</Typography><ArrowForward fontSize="small" /></Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Box sx={{ mt: { xs: 5, md: 7 } }}>
          <Typography variant="h3" component="h2" sx={{ fontSize: { xs: "1.8rem", md: "2.4rem" } }}>Twoje zestawy do powtórki</Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>Te listy są zapisywane na tym urządzeniu.</Typography>
          <Grid container spacing={2} sx={{ mt: 1.5 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3, height: "100%", border: "1px solid rgba(23,52,52,.1)", borderRadius: 1.5, bgcolor: "rgba(255,255,255,.72)" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 1, display: "grid", placeItems: "center", color: "primary.main", bgcolor: "rgba(45,92,88,.1)" }}><Replay /></Box>
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>Powtórka błędów</Typography>
                <Typography color="text.secondary" sx={{ mt: .5 }}>Pytania, na które odpowiedziałeś niepoprawnie. Zapisano: {incorrectCount}.</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                  <Button onClick={onStartIncorrectPractice} disabled={!incorrectCount} variant="contained">Ćwicz</Button>
                  <Button onClick={onStartIncorrectTest} disabled={!incorrectCount} variant="outlined">Próbny egzamin</Button>
                </Stack>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3, height: "100%", border: "1px solid rgba(23,52,52,.1)", borderRadius: 1.5, bgcolor: "rgba(255,255,255,.72)" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 1, display: "grid", placeItems: "center", color: "primary.main", bgcolor: "rgba(45,92,88,.1)" }}><Bookmark /></Box>
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 800 }}>Zapisane pytania</Typography>
                <Typography color="text.secondary" sx={{ mt: .5 }}>Dodaj zakładkę przy pytaniu, aby umieścić je we własnym teście. Zapisano: {savedCount}.</Typography>
                <Button onClick={onStartSavedTest} disabled={!savedCount} variant="contained" sx={{ mt: 2 }}>Rozpocznij test</Button>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Paper elevation={0} sx={{ mt: { xs: 5, md: 8 }, p: { xs: 3, md: 4 }, borderRadius: 1.5, bgcolor: "#e3efe9", display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, alignItems: { md: "center" }, justifyContent: "space-between" }}>
          <Stack direction="row" spacing={2} alignItems="center"><Box sx={{ width: 48, height: 48, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "#fff", color: "primary.main" }}><Explore /></Box><Box><Typography sx={{ fontWeight: 800 }}>Szukasz miejsca do latania?</Typography><Typography variant="body2" color="text.secondary">Odkryj startowiska paralotniowe w Launch Point.</Typography></Box></Stack>
          <Button href="https://launchpoint.cloud" target="_blank" rel="noopener noreferrer" variant="outlined" sx={{ alignSelf: { xs: "flex-start", md: "auto" }, borderColor: "primary.main", color: "primary.main" }}>Odwiedź Launch Point</Button>
        </Paper>
      </Container>
    </Box>
  );
}

export default StartScreen;
